import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Card,
    CardContent,
    Divider,
    Tooltip,
    IconButton,
    Collapse,
} from '@mui/material';
import { Edit as EditIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useLanguages, useUpdateLanguage, useSyncMeta } from '../../hooks/useSettings';
import type { LanguageSpecItem } from '../../types/settings';
import { formatKstDateTime } from '../../utils/dateTime';

const FLAG_BASE = 'https://flagcdn.com/20x15';

const formatDate = (dateStr?: string) => {
    return formatKstDateTime(dateStr);
};

export const SystemSettings: FC = () => {
    const { data: languages, isLoading: langsLoading } = useLanguages();
    const { data: syncMeta, isLoading: syncLoading } = useSyncMeta();
    const updateLanguage = useUpdateLanguage();

    const [editTarget, setEditTarget] = useState<LanguageSpecItem | null>(null);
    const [editSortOrder, setEditSortOrder] = useState('');
    const [editIsActive, setEditIsActive] = useState(true);
    const [expandedCode, setExpandedCode] = useState<string | null>(null);

    const handleOpenEdit = (lang: LanguageSpecItem) => {
        setEditTarget(lang);
        setEditSortOrder(lang.sortOrder.toString());
        setEditIsActive(lang.isActive);
    };

    const handleSave = () => {
        if (!editTarget) return;
        updateLanguage.mutate(
            { code: editTarget.code, data: { isActive: editIsActive, sortOrder: parseInt(editSortOrder) || 0 } },
            { onSuccess: () => setEditTarget(null) },
        );
    };

    const handleToggleActive = (lang: LanguageSpecItem) => {
        updateLanguage.mutate({
            code: lang.code,
            data: { isActive: !lang.isActive, sortOrder: lang.sortOrder },
        });
    };

    return (
        <Box p={3}>
            <Typography variant="h4" sx={{ mb: 4 }}>시스템 설정</Typography>

            {/* ─── 언어 설정 ─── */}
            <Typography variant="h5" sx={{ mb: 2 }}>언어 설정</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                앱에서 지원하는 언어 목록을 관리합니다. 비활성화하면 새 사용자에게 해당 언어가 표시되지 않습니다.
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 5 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>국기</TableCell>
                            <TableCell>코드</TableCell>
                            <TableCell>네이티브 이름</TableCell>
                            <TableCell>한국어 이름</TableCell>
                            <TableCell>Locale ID</TableCell>
                            <TableCell>정렬 순서</TableCell>
                            <TableCell>다국어 이름</TableCell>
                            <TableCell>활성화</TableCell>
                            <TableCell align="center">편집</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {langsLoading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>로딩 중...</TableCell>
                            </TableRow>
                        ) : (
                            (languages ?? []).map((lang) => (
                                <>
                                    <TableRow key={lang.code} hover sx={{ opacity: lang.isActive ? 1 : 0.5 }}>
                                        <TableCell>
                                            <img
                                                src={`${FLAG_BASE}/${lang.country.toLowerCase()}.png`}
                                                alt={lang.country}
                                                style={{ display: 'block' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={lang.code} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{lang.nativeName}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{lang.localizedNames['ko'] ?? '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">{lang.localeId}</Typography>
                                        </TableCell>
                                        <TableCell>{lang.sortOrder}</TableCell>
                                        <TableCell>
                                            <Tooltip title="다국어 이름 펼치기">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setExpandedCode(expandedCode === lang.code ? null : lang.code)}
                                                >
                                                    {expandedCode === lang.code ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={lang.isActive}
                                                size="small"
                                                onChange={() => handleToggleActive(lang)}
                                                disabled={updateLanguage.isPending}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleOpenEdit(lang)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    {/* 다국어 이름 펼치기 */}
                                    <TableRow key={`${lang.code}-expand`}>
                                        <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                                            <Collapse in={expandedCode === lang.code} timeout="auto" unmountOnExit>
                                                <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc' }}>
                                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                                        {Object.entries(lang.localizedNames)
                                                            .sort(([a], [b]) => a.localeCompare(b))
                                                            .map(([langCode, name]) => (
                                                                <Chip
                                                                    key={langCode}
                                                                    label={`${langCode}: ${name}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.72rem' }}
                                                                />
                                                            ))}
                                                    </Stack>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            {/* ─── 동기화 상태 ─── */}
            <Typography variant="h5" sx={{ mb: 2 }}>동기화 상태</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                클라이언트 데이터 동기화의 현재 상태입니다. (읽기 전용)
            </Typography>

            {syncLoading ? (
                <Typography color="text.secondary">로딩 중...</Typography>
            ) : syncMeta ? (
                <Card sx={{ maxWidth: 480 }}>
                    <CardContent>
                        <Stack spacing={2}>
                            <InfoRow label="Epoch" value={syncMeta.currentEpoch.toString()} />
                            <Divider />
                            <InfoRow label="현재 Revision" value={syncMeta.currentRevision.toLocaleString()} highlight />
                            <Divider />
                            <InfoRow label="마지막 Purge" value={formatDate(syncMeta.lastPurgeAt)} />
                            <Divider />
                            <InfoRow label="마지막 업데이트" value={formatDate(syncMeta.updatedAt)} />
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {/* 언어 편집 Dialog */}
            <Dialog open={editTarget !== null} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>언어 설정 편집 — {editTarget?.nativeName}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">언어 코드</Typography>
                            <Typography variant="body1" fontWeight={600}>{editTarget?.code}</Typography>
                        </Box>
                        <TextField
                            label="정렬 순서"
                            type="number"
                            value={editSortOrder}
                            onChange={(e) => setEditSortOrder(e.target.value)}
                            fullWidth
                            helperText="숫자가 작을수록 앞에 표시됩니다"
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                            <Switch
                                checked={editIsActive}
                                onChange={(e) => setEditIsActive(e.target.checked)}
                            />
                            <Typography variant="body2">{editIsActive ? '활성화됨' : '비활성화됨'}</Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditTarget(null)}>취소</Button>
                    <Button variant="contained" onClick={handleSave} disabled={updateLanguage.isPending}>
                        저장
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const InfoRow: FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={highlight ? 700 : 400} color={highlight ? 'primary.main' : 'text.primary'}>
            {value}
        </Typography>
    </Box>
);
